"use client"

import * as React from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { UserProfile, Account, Language } from "@/types/workspace"
import { NotificationItem } from "@/types/notification"
import { User, Settings, Image as ImageIcon, Plus, Bell, Sun, Moon, Globe, ChevronRight, Home } from "lucide-react"

interface MobileControlCenterProps {
  isOpen: boolean
  onClose: () => void
  user: UserProfile | null
  accounts: Account[]
  currentAccount: Account | null
  lang: Language
  setLang: (lang: Language) => void
  theme: string
  setTheme: (theme: string) => void
  notifications: NotificationItem[]
  handleLogout: () => void
  handleSwitchAccount: (id: string) => void
  handleOpenProfileTab: () => void
  setProfileTab: (tab: string) => void
  handleOpenSettingsTab: () => void
  setShowWallpaperModal: (open: boolean) => void
  setShowAddAppModal: (open: boolean) => void
  setShowMobileNotifications: (open: boolean) => void
}

export function MobileControlCenter({
  isOpen,
  onClose,
  user,
  accounts,
  currentAccount,
  lang,
  setLang,
  theme,
  setTheme,
  notifications,
  handleLogout,
  handleSwitchAccount,
  handleOpenProfileTab,
  setProfileTab,
  handleOpenSettingsTab,
  setShowWallpaperModal,
  setShowAddAppModal,
  setShowMobileNotifications,
}: MobileControlCenterProps) {
  const router = useRouter()
  if (!isOpen) return null

  const isLight = theme === "light"

  return (
    <div className={`fixed inset-0 z-55 flex flex-col backdrop-blur-xl animate-in slide-in-from-top duration-300 font-sans p-6 select-none ${
      isLight ? "bg-zinc-100/95 text-zinc-900 border-zinc-200" : "bg-zinc-950/90 text-white border-zinc-800"
    }`}>
      {/* Top Handle bar / Drag indicator to close */}
      <div className="flex flex-col items-center gap-1.5 cursor-pointer pb-4" onClick={onClose}>
        <div className={`w-12 h-1 rounded-full ${isLight ? "bg-zinc-300" : "bg-white/30"}`} />
        <span className={`text-[10px] font-bold uppercase tracking-wider ${isLight ? "text-zinc-500" : "text-white/50"}`}>Central de Controle</span>
      </div>

      {/* Informações de Perfil do Usuário */}
      <div className={`flex items-center gap-3 rounded-2xl p-3 border mb-4 shadow-sm ${
        isLight ? "bg-white border-zinc-200/80 text-zinc-900" : "bg-white/10 border-white/5 text-white"
      }`}>
        <div className="size-10 rounded-full overflow-hidden bg-[#004EB1] flex items-center justify-center font-bold text-white uppercase select-none shrink-0">
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
          ) : (
            user?.name?.substring(0, 2) || "AD"
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`text-xs font-bold truncate ${isLight ? "text-zinc-900" : "text-white"}`}>{user?.name || "Administrador"}</h4>
          <p className={`text-[9px] truncate ${isLight ? "text-zinc-500" : "text-white/50"}`}>{user?.email || "admin@bold.site"}</p>
        </div>
        <button
          onClick={() => {
            handleLogout()
            onClose()
          }}
          className="px-2.5 py-1 rounded-lg bg-red-500/15 text-red-500 text-[10px] font-bold border border-red-500/30 hover:bg-red-500/25 active:scale-95 cursor-pointer shrink-0"
        >
          Sair
        </button>
      </div>

      {/* Botão Ir para a Tela Inicial */}
      <button
        onClick={() => {
          onClose()
          router.push("/")
        }}
        className={`w-full mb-4 p-3 rounded-2xl border flex items-center justify-between font-semibold text-xs transition-all cursor-pointer ${
          isLight ? "bg-white border-zinc-200 text-zinc-900 shadow-sm" : "bg-white/10 border-white/10 text-white"
        }`}
      >
        <div className="flex items-center gap-2.5">
          <Home className="size-4 text-[#004EB1] dark:text-blue-400" />
          <span>Tela Inicial (Hub de Empresas)</span>
        </div>
        <ChevronRight className="size-4 text-zinc-400" />
      </button>

      {/* Workspace Switcher */}
      <div className={`rounded-2xl p-3 border mb-4 space-y-2 shadow-sm ${
        isLight ? "bg-white border-zinc-200/80" : "bg-white/10 border-white/5"
      }`}>
        <div className={`text-[10px] font-bold uppercase tracking-wider ${isLight ? "text-zinc-500" : "text-white/40"}`}>Mudar Empresa / Workspace</div>
        <div className="max-h-24 overflow-y-auto space-y-1 pr-1 scrollbar-none">
          {accounts.map((acc) => {
            const isActive = acc.id === currentAccount?.id
            return (
              <button
                key={acc.id}
                onClick={() => {
                  handleSwitchAccount(acc.id)
                  onClose()
                }}
                className={`w-full text-left p-2 rounded-lg text-[11px] font-semibold flex items-center justify-between border transition-all cursor-pointer ${
                  isActive
                    ? "bg-[#004EB1]/15 border-[#004EB1]/40 text-[#004EB1] font-bold"
                    : isLight ? "bg-zinc-50 border-zinc-200 text-zinc-700 hover:bg-zinc-100" : "bg-white/5 border-transparent text-white/70 hover:bg-white/10 hover:text-white"
                }`}
              >
                <span className="truncate">{acc.name}</span>
                {isActive && <span className="text-[9px] font-bold bg-[#004EB1] px-1.5 py-0.5 rounded text-white">Ativo</span>}
              </button>
            )
          })}
        </div>
      </div>

      {/* Idioma & Tema Selector */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className={`rounded-2xl p-3 border flex flex-col justify-between gap-2 shadow-sm ${
          isLight ? "bg-white border-zinc-200/80" : "bg-white/10 border-white/5"
        }`}>
          <div className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${isLight ? "text-zinc-500" : "text-white/40"}`}>
            <Globe className="size-3 text-blue-500" /> Idioma
          </div>
          <div className="flex flex-col gap-1">
            {(
              [
                { code: "pt", label: "PT - Português" },
                { code: "en", label: "EN - English" },
                { code: "es", label: "ES - Español" },
              ] as { code: Language; label: string }[]
            ).map((l) => (
              <button
                key={l.code}
                onClick={() => {
                  setLang(l.code)
                  localStorage.setItem("language", l.code)
                }}
                className={`w-full text-left px-2 py-1 rounded text-[10px] font-semibold transition-all cursor-pointer ${
                  lang === l.code 
                    ? "bg-[#004EB1] text-white font-bold" 
                    : isLight ? "bg-zinc-100 text-zinc-700 hover:bg-zinc-200" : "bg-white/5 text-white/60 hover:bg-white/10"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <div className={`rounded-2xl p-3 border flex flex-col justify-between shadow-sm ${
          isLight ? "bg-white border-zinc-200/80" : "bg-white/10 border-white/5"
        }`}>
          <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isLight ? "text-zinc-500" : "text-white/40"}`}>Tema Visual</div>
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className={`w-full flex items-center justify-center gap-2 p-2.5 rounded-xl border text-xs font-semibold active:scale-95 transition-all cursor-pointer ${
              isLight ? "bg-zinc-100 hover:bg-zinc-200 border-zinc-200 text-zinc-800" : "bg-white/5 border-white/10 text-white hover:bg-white/10"
            }`}
          >
            {theme === "dark" ? <Sun className="size-4 text-amber-500 shrink-0" /> : <Moon className="size-4 text-blue-500 shrink-0" />}
            <span>{theme === "dark" ? "Modo Claro" : "Modo Escuro"}</span>
          </button>
          <div className={`text-[9px] mt-2 text-center ${isLight ? "text-zinc-500" : "text-white/40"}`}>Toque para alternar o contraste da tela</div>
        </div>
      </div>

      {/* Ajustes & Personalização */}
      <div className={`rounded-2xl p-3 border mb-4 space-y-2 shadow-sm ${
        isLight ? "bg-white border-zinc-200/80" : "bg-white/10 border-white/5"
      }`}>
        <div className={`text-[10px] font-bold uppercase tracking-wider ${isLight ? "text-zinc-500" : "text-white/40"}`}>Ajustes & Personalização</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              handleOpenProfileTab()
              setProfileTab("profile")
            }}
            className={`p-2.5 rounded-xl border text-[11px] font-bold text-left flex items-center gap-2 cursor-pointer active:scale-95 transition-all ${
              isLight ? "bg-zinc-50 border-zinc-200 hover:bg-zinc-100 text-zinc-800" : "bg-white/5 border-white/5 hover:bg-white/10 text-white"
            }`}
          >
            <User className="size-3.5 text-blue-500 shrink-0" />
            <span>Meu Perfil</span>
          </button>

          <button
            onClick={() => {
              handleOpenSettingsTab()
              onClose()
            }}
            className={`p-2.5 rounded-xl border text-[11px] font-bold text-left flex items-center gap-2 cursor-pointer active:scale-95 transition-all ${
              isLight ? "bg-zinc-50 border-zinc-200 hover:bg-zinc-100 text-zinc-800" : "bg-white/5 border-white/5 hover:bg-white/10 text-white"
            }`}
          >
            <Settings className="size-3.5 text-blue-500 shrink-0" />
            <span>Ajustes</span>
          </button>

          <button
            onClick={() => {
              setShowWallpaperModal(true)
              onClose()
            }}
            className={`p-2.5 rounded-xl border text-[11px] font-bold text-left flex items-center gap-2 cursor-pointer active:scale-95 transition-all ${
              isLight ? "bg-zinc-50 border-zinc-200 hover:bg-zinc-100 text-zinc-800" : "bg-white/5 border-white/5 hover:bg-white/10 text-white"
            }`}
          >
            <ImageIcon className="size-3.5 text-blue-500 shrink-0" />
            <span>Wallpaper</span>
          </button>

          <button
            onClick={() => {
              setShowAddAppModal(true)
              onClose()
            }}
            className={`p-2.5 rounded-xl border text-[11px] font-bold text-left flex items-center gap-2 cursor-pointer active:scale-95 transition-all ${
              isLight ? "bg-zinc-50 border-zinc-200 hover:bg-zinc-100 text-zinc-800" : "bg-white/5 border-white/5 hover:bg-white/10 text-white"
            }`}
          >
            <Plus className="size-3.5 text-blue-500 shrink-0" />
            <span>Gerenciar Apps</span>
          </button>
        </div>
      </div>

      {/* Atalho para Notificações */}
      <button
        onClick={() => {
          onClose()
          setShowMobileNotifications(true)
        }}
        className={`flex items-center justify-between w-full rounded-2xl p-3 border active:scale-95 transition-all cursor-pointer shadow-sm ${
          isLight ? "bg-white border-zinc-200/80 text-zinc-900 hover:bg-zinc-50" : "bg-white/10 border-white/5 text-white hover:bg-white/15"
        }`}
      >
        <div className="flex items-center gap-2.5">
          <div
            className={`size-8 rounded-xl flex items-center justify-center ${
              notifications.length > 0 ? "bg-blue-500/20" : isLight ? "bg-zinc-100" : "bg-white/10"
            }`}
          >
            <Bell className="size-4 text-blue-500 shrink-0" />
          </div>
          <div className="text-left">
            <div className={`text-xs font-bold ${isLight ? "text-zinc-900" : "text-white"}`}>Notificações</div>
            <div className={`text-[9px] ${isLight ? "text-zinc-500" : "text-white/50"}`}>
              {notifications.length > 0
                ? `${notifications.length} nova${notifications.length > 1 ? "s" : ""}`
                : "Nenhuma pendente"}
            </div>
          </div>
        </div>
        <ChevronRight className={`size-4 ${isLight ? "text-zinc-400" : "text-white/40"}`} />
      </button>

      {/* Close Button */}
      <Button
        onClick={onClose}
        className={`mt-4 w-full rounded-2xl py-3 border font-bold text-sm cursor-pointer shadow-lg ${
          isLight ? "bg-zinc-200 hover:bg-zinc-300 text-zinc-900 border-zinc-300" : "bg-white/10 hover:bg-white/20 text-white border-white/10"
        }`}
      >
        Fechar
      </Button>
    </div>
  )
}
